import { Accordion, Button, Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import { push } from '@lagunovsky/redux-react-router'
import { capitalize, compact, flattenDeep, groupBy, includes, isEmpty, isUndefined, keys, orderBy, pick, uniq, values } from 'lodash'
import Moment from 'moment'
import { extendMoment } from 'moment-range'
import 'moment/locale/fr'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import i18n from 'simple-react-i18n'
import { SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import { CardTitle } from 'components/card/NewCard'
import { NoDataPanel } from 'components/card/NoDataCard'
import { CardTable, SORT } from 'components/datatable/NewTable'
import Input from 'components/forms/Input'
import Select from 'components/forms/Select'
import SimpleTabList from 'components/list/SimpleTabList'
import ExportFileModal from 'components/modal/ExportFileModal'
import ProgressBar from 'components/progress/ProgressBar'
import { AccordionTitle } from 'components/styled/Accordions'
import ExportAction from 'export/actions/ExportAction'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import { promiseAllProgress } from 'utils/ActionUtils'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useActions from 'utils/customHook/useActions'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import useBoolean from 'utils/customHook/useBoolean'
import useCampaignObjectives from 'utils/customHook/useCampaignObjectives'
import useListIndexed from 'utils/customHook/useListIndexed'
import useTitle from 'utils/customHook/useTitle'
import { formatData } from 'utils/ExportDataUtil'
import { hybridSearch } from 'utils/SearchUtil'
import { getI18nTitleDataLength, normalizeString } from 'utils/StringUtil'
import { PASTEL_COLOR } from 'utils/constants/ColorTheme'
import { campaignShape, stationCampaignPlanningShape } from '../../shapes/CampaignShapes'
import CampaignEventsModal from '../CampaignEventsModal'
import CampaignProgress from './CampaignProgress'
import CartographyPanel from '../../../components/map/CartographyPanel'
import DtoHydrometricStation from '../../../hydrometry/dto/DtoHydrometricStation'
import DtoInstallation from '../../../installation/dto/installation/DtoInstallation'
import DtoPiezometerLight from '../../../piezometry/dto/DtoPiezometerLight'
import PluviometerDto from '../../../pluviometry/dto/PluviometerDto'
import { getDate } from '../../../utils/DateUtil'
import { hasValue } from '../../../utils/NumberUtil'
import CampaignAction from '../../actions/CampaignAction'
import { getCampaignPeriodTitle, getMarkerIcon, getPlanningPeriodTitle, getPlanningProducers, getPlanningProducersName } from '../../utils/CampaignUtils'

const moment = extendMoment(Moment)
moment.locale('fr')

const LIST = 'LIST'
const CARTO = 'CARTO'
const NOT_SPECIFIED_PRODUCER = -1

const CAMPAIGN_BAND = -1

const getNbVisitsExpected = stationProgress => stationProgress.nbVisitsExpected ?? 1

const hasObjectives = stationProgress => !isEmpty(stationProgress.plannedObjectives)

const isCompleted = stationProgress => {
    if (!stationProgress.nbEventsMatchingType) return false
    return hasObjectives(stationProgress)
        ? isEmpty(stationProgress.missingObjectives)
        : stationProgress.nbEventsMatchingType >= getNbVisitsExpected(stationProgress)
}

const getColorAndRank = (stationProgress, nbDone) => {
    if (!nbDone && hasValue(stationProgress.causeNonRealisation)) return [PASTEL_COLOR.grey, 1]
    if (!stationProgress.nbEvents) return [PASTEL_COLOR.white, 2]
    if (isCompleted(stationProgress)) return [PASTEL_COLOR.green, 3]
    return [PASTEL_COLOR.orange, 0]
}

const getStationState = stationProgress => {
    const withObjectives = hasObjectives(stationProgress)
    const nbExpected = withObjectives ? stationProgress.plannedObjectives.length : getNbVisitsExpected(stationProgress)
    const nbDone = withObjectives ? nbExpected - stationProgress.missingObjectives.length : stationProgress.nbEventsMatchingType
    const nbMissing = Math.max(nbExpected - nbDone, 0)
    const [color, rank] = getColorAndRank(stationProgress, nbDone)
    return {
        value: `${nbDone}/${nbExpected}`,
        missingRatio: nbExpected === 0 ? 1 : nbMissing / nbExpected,
        color,
        rank,
    }
}

const bandTitle = (planning, producerName) => {
    const period = getPlanningPeriodTitle(planning.startDate, planning.endDate)
    return producerName ? `${period} - ${producerName}` : period
}

const TableStation = ({
    planning = {},
    planningProgress = {},
    campaignStations = [],
    stations = [],
    stationType = '',
    campaign = {},
    campaignEvents = [],
}) => {
    const dispatch = useDispatch()

    const {
        citiesIndex,
        contacts,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const indexedStations = useListIndexed(stations, 'id')

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const {
        value: isOpen,
        setTrue: openPopin,
        setFalse: closePopin,
    } = useBoolean(false)
    const [selectedStationId, setSelectedStationId] = useState()

    const { stationsProgress = [] } = planningProgress

    const listStations = campaignStations.filter(cs => indexedStations[cs.stationId] && stationsProgress.find(s => s.id === cs.stationId)).map(({ stationId }) => stationId)

    const states = listStations.reduce((acc, id) => ({ ...acc, [id]: getStationState(stationsProgress.find(s => s.id === id)) }), {})

    const completedStation = listStations.filter(id => isCompleted(stationsProgress.find(sp => sp.id === id))).length

    const producerName = getPlanningProducersName(planning, contacts)

    const row = [{
        ...listStations.reduce((acc, id) => {
            acc[id] = {
                value: states[id].value,
                color: states[id].color,
                onClick: () => {
                    setSelectedStationId(id)
                    openPopin()
                },
            }
            return acc
        }, {}),
    }]

    const headers = [
        ...orderBy(listStations, [id => states[id].rank, id => states[id].missingRatio], ['asc', 'asc']).map(id => {
            const station = indexedStations[id]
            return {
                key: `${id}`,
                maxWidth: 150,
                value: (
                    <Stack>
                        {station?.code ?? ''}
                        <Typography sx={{ fontWeight: 'normal', fontSize: 12 }}>{station?.name ?? ''}</Typography>
                    </Stack>
                ),
                tooltip: (
                    <Stack>
                        <Typography>{showInseeCode ? citiesIndex[station.townCode]?.labelWithCode : citiesIndex[station.townCode]?.name}</Typography>
                        <Typography>{station?.name ?? ''}</Typography>
                    </Stack>
                ),
                onClick: () => dispatch(push(`/station/${stationType}/${id}/event`)),
            }
        }),
    ]

    const selectedStation = hasValue(selectedStationId) ? indexedStations[selectedStationId] : undefined
    const selectedProgress = stationsProgress.find(s => s.id === selectedStationId)

    return (
        <>
            <CardTable
                title={bandTitle(planning, producerName)}
                actions={[<CampaignProgress key='progress' done={completedStation} total={listStations.length} />]}
                rows={row}
                headers={headers}
                hideNbElements

                defaultSort={{ column: 'planning', direction: SORT.ASC }}
                rowsPerPageOptions={nbPerPageLabel}
            />
            <CampaignEventsModal
                events={campaignEvents.filter(event => event.stationId === selectedStationId)}
                isOpen={isOpen}
                onClose={closePopin}
                stationType={stationType}
                date={planning.startDate}
                campaignCode={campaign.id}
                campaignName={campaign.name}
                campaignType={campaign.campaignType}
                stationId={selectedStationId}
                strDate={bandTitle(planning, producerName)}
                stationLabel={`[${selectedStation?.code ?? ''}] ${selectedStation?.name ?? ''}`}
                objectives={selectedProgress?.missingObjectives}
            />
        </>
    )
}

TableStation.propTypes = {
    planning: stationCampaignPlanningShape,
    planningProgress: PropTypes.shape({
        id: PropTypes.number,
        stationsProgress: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            missingObjectives: PropTypes.arrayOf(PropTypes.number),
        })),
    }),
    campaignStations: PropTypes.arrayOf(PropTypes.shape({})),
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    stationType: PropTypes.string,
    campaign: campaignShape,
    campaignEvents: PropTypes.arrayOf(PropTypes.shape({})),
}

const CartoStation = ({
    campaignStations = [],
    stations = [],
    stationType = '',
}) => {
    const stationsPoints = compact(campaignStations.map(cs => {
        const station = stations.find(s => s.id === cs.stationId)
        return station && { ...station, markerIcon: getMarkerIcon(stationType) }
    }))

    return (
        <CartographyPanel
            layers={['STATIONS_POINTS']}
            componentType={stationType}
            stationsPoints={stationsPoints}
            stationsPanelTitle={i18n[stationType]}
            height={750}
        />
    )
}

CartoStation.propTypes = {
    campaignStations: PropTypes.arrayOf(PropTypes.shape({})),
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    stationType: PropTypes.string,
}

const ExportModal = ({
    isOpen = false,
    close = () => {},

    plannings = [],
    planningsProgress = [],
    campaignStations = [],
    stations = [],
    objectiveList = [],
}) => {
    const dispatch = useDispatch()

    const indexedStations = useListIndexed(stations, 'id')

    const onExportTable = (exportFormat) => {
        const listStations = campaignStations.filter(cs => indexedStations[cs.stationId]).map(cs => cs.stationId)

        const rows = plannings.map(planning => {
            const { stationsProgress = [] } = planningsProgress.find(pp => pp.id === planning.id) ?? {}
            const listStationsByPlanning = campaignStations.filter(cs => indexedStations[cs.stationId] && stationsProgress.find(s => s.id === cs.stationId)).map(({ stationId }) => stationId)

            const completedStation = stationsProgress.reduce((nb, sp) => isCompleted(sp) ? nb + 1 : nb, 0)
            return {
                planning: `${getDate(planning.startDate)} - ${getDate(planning.endDate)}`,
                executed: `${completedStation}/${listStationsByPlanning.length}`,
                ...listStations.reduce((acc, id) => {
                    const sp = stationsProgress.find(s => s.id === id)
                    acc[id] = sp ? pick(getStationState(sp), ['value', 'color']) : { value: '', color: PASTEL_COLOR.white }
                    return acc
                }, {}),
            }
        })

        const headers = [
            'planning',
            'executed',
            ...listStations.map(id => `${id}`),
        ]

        const customHeaders = [
            'planning',
            'executed',
            ...listStations.map(id => {
                const station = indexedStations[id]
                return `[${station?.code ?? ''}] ${station?.name ?? ''}`
            }),
        ]

        const dataWithHeaders = rows.length ? [
            { ...rows[0], headers, customHeaders },
            ...rows.slice(1),
        ] : []

        dispatch(ExportAction.export(formatData(dataWithHeaders), exportFormat, i18n.tracking))
    }

    const onExportObjectivesMissing = (exportFormat) => {
        const rows = plannings.map(planning => {
            const { stationsProgress = [] } = planningsProgress.find(pp => pp.id === planning.id) ?? {}

            return stationsProgress.map(sp => sp.missingObjectives.map(objectiveCode => ({
                stationCode: { value: indexedStations[sp.id]?.code, cellType: 'string' },
                stationName: indexedStations[sp.id]?.name,
                startDate: getDate(planning.startDate),
                endDate: getDate(planning.endDate),
                objectiveCode,
                objective: objectiveList.find(({ code }) => code === objectiveCode)?.name,
            })))
        })

        const flattenRows = flattenDeep(rows)

        const dataWithHeaders = flattenRows.length ? [
            { ...flattenRows[0], headers: keys(flattenRows[0]) },
            ...flattenRows.slice(1),
        ] : []

        dispatch(ExportAction.export(formatData(dataWithHeaders), exportFormat, i18n.tracking))
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={close}
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExportTable('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExportTable('csv'),
                    }],
                },
                {
                    name: i18n.missingObjective,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExportObjectivesMissing('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExportObjectivesMissing('csv'),
                    }],
                },
            ]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool,
    close: PropTypes.func,
    plannings: PropTypes.arrayOf(stationCampaignPlanningShape),
    planningsProgress: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        stationsProgress: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            missingObjectives: PropTypes.arrayOf(PropTypes.number),
        })),
    })),
    campaignStations: PropTypes.arrayOf(PropTypes.shape({})),
    stations: PropTypes.arrayOf(PropTypes.shape({})),
    objectiveList: PropTypes.arrayOf(PropTypes.shape({})),
}

const CampaignTracking = ({
    stationType,
    stations = [],
}) => {
    const { id } = useParams()

    const {
        campaign,
        campaignStations,
        stationCampaignPlannings,
        campaignEvents,
        contacts,
    } = useSelector(store => ({
        campaign: store.CampaignReducer.campaign,
        campaignStations: store.CampaignReducer.campaignStations,
        stationCampaignPlannings: store.CampaignReducer.stationCampaignPlannings,
        campaignEvents: store.CampaignReducer.campaignEvents,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const objectiveList = useCampaignObjectives(campaign.campaignType)

    const [planningsProgress, setPlanningsProgress] = useState([])
    const [progress, setProgress] = useState(0)
    const [searchValue, setSearchValue] = useState()
    const [selectedProducer, setSelectedProducer] = useState()

    const {
        value: isLoading,
        setTrue: onStartLoading,
        setFalse: onEndLoading,
    } = useBoolean(true)

    const {
        value: isExportOpen,
        setFalse: closeExport,
        setTrue: openExport,
    } = useBoolean(false)

    useEffect(() => {
        onStartLoading()
        setProgress(0)
        setPlanningsProgress([])
        const promises = stationCampaignPlannings.length === 0
            ? [CampaignAction.getStationFollowUp(stationType, id).then((pps = []) => setPlanningsProgress(p => [...p, pps]))]
            : stationCampaignPlannings.map(cp => CampaignAction.getStationPlanningFollowUp(stationType, id, cp.id).then((pps = []) => setPlanningsProgress(p => [...p, pps])))
        promiseAllProgress(promises, setProgress).finally(onEndLoading)
    }, [id, stationType, stationCampaignPlannings])

    const startDate = moment(campaign.beginningApplication, 'DD/MM/YYYY').valueOf()
    const endDate = hasValue(campaign.endingApplication) ? moment(campaign.endingApplication, 'DD/MM/YYYY').valueOf() : undefined

    const plannings = useMemo(() => {
        const searchStations = campaignStationList => {
            const formattedStations = compact(campaignStationList
                .map(({ stationId }) => stations.find(({ id: currentId }) => currentId === stationId)),
            ).map(station => ({
                ...station,
                _normalizedSearchString: values(pick(station, ['code', 'name', 'townCode'])).map(s => normalizeString(s)),
            }))
            return hybridSearch(formattedStations, normalizeString(searchValue)).map(({ id: currentId }) => currentId)
        }

        if (stationCampaignPlannings.length > 0) {
            return stationCampaignPlannings.map(planning => ({
                ...planning,
                stationsPlan: searchStations(compact(planning.stationsPlan.map(campaignStationId => campaignStations.find(cs => cs.id === campaignStationId)))),
            }))
        }
        return [{
            campaignCode: campaign.id,
            id: CAMPAIGN_BAND,
            startDate,
            endDate,
            stationsPlan: searchStations(campaignStations),
        }]
    }, [campaign.id, campaignStations, stationCampaignPlannings, endDate, startDate, stations, searchValue])

    const filteredPlannings = useMemo(() => plannings.filter(planning => {
        const producers = getPlanningProducers(planning)
        return !isEmpty(planning.stationsPlan) && (
            selectedProducer === NOT_SPECIFIED_PRODUCER ? isEmpty(producers) : isUndefined(selectedProducer) || includes(producers, selectedProducer)
        )
    }), [plannings, selectedProducer])

    useTitle(() => [
        {
            title: i18n[stationType],
            href: `/${stationType}`,
        },
        {
            title: i18n.campaigns,
            href: `/${stationType}/campaign`,
        },
        {
            title: campaign.name || id,
            href: `/${stationType}/campaign/${id}/dashboard`,
        },
        {
            title: i18n.tracking,
            href: `/${stationType}/campaign/${id}/tracking`,
        },
    ], [campaign, id, stationType])

    useActions(() => ({
        exportList: [{
            onClick: openExport,
            label: i18n.excel,
        }],
    }), [])

    const groupedPlannings = groupBy(filteredPlannings, p => moment(p.startDate).month())

    if (isLoading) {
        return (
            <Stack padding={1}>
                <Card sx={{ padding: 1 }}>
                    <ProgressBar withMessage indeterminate={stationCampaignPlannings.length <= 1} progress={progress} />
                </Card>
            </Stack>
        )
    }

    return (
        <Grid container sx={{ padding: '5 10' }} spacing={2}>
            <Grid size={12}>
                <SimpleTabList
                    defaultTab={LIST}
                    tabs={[
                        {
                            constant: LIST,
                            label: i18n.table,
                            icon: 'list',
                        },
                        {
                            constant: CARTO,
                            label: i18n.map,
                            icon: 'map',
                        },
                    ]}
                    headerComponent={(
                        <Card>
                            <CardTitle title={getCampaignPeriodTitle(campaign.beginningApplication, campaign.endingApplication)} />
                            <CardContent>
                                <Grid container spacing={1}>
                                    <Grid size={6}>
                                        <Input
                                            title={i18n.search}
                                            value={searchValue}
                                            onChange={setSearchValue}
                                        />
                                    </Grid>
                                    <Grid size={'grow'}>
                                        <Select
                                            label={i18n.completion}
                                            options={[
                                                { name: i18n.notSpecified, code: NOT_SPECIFIED_PRODUCER },
                                                ...contacts.filter(({ id: contactId }) => includes(uniq(plannings.flatMap(getPlanningProducers)).map(code => parseInt(code)), contactId)),
                                            ]}
                                            noSort
                                            value={selectedProducer}
                                            onChange={setSelectedProducer}
                                        />
                                    </Grid>
                                    <Grid offset={11} size={1}>
                                        <Button
                                            variant={'contained'}
                                            fullWidth
                                            onClick={() => {
                                                setSearchValue(undefined)
                                                setSelectedProducer(undefined)
                                            }}
                                        >
                                            {i18n.reinit}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}
                >
                    {
                        tab => (
                            <>
                                {tab === LIST && (
                                    <Stack spacing={2}>
                                        {isEmpty(groupedPlannings) ?
                                            <NoDataPanel />
                                            :
                                            keys(groupedPlannings).map((month, i) => (
                                                <Accordion key={i} defaultExpanded={moment().month() === parseInt(month)}>
                                                    <AccordionTitle
                                                        title={capitalize(moment().month(month).format('MMMM'))}
                                                        subTitle={`${groupedPlannings[month].length} ${getI18nTitleDataLength(i18n.planning, i18n.plannings, groupedPlannings[month].length)}`}
                                                    />
                                                    <Stack spacing={2} padding={1}>
                                                        {orderBy(groupedPlannings[month], 'startDate', 'asc').map((p, j) => (
                                                            <TableStation
                                                                key={j}
                                                                planning={p}
                                                                planningProgress={planningsProgress.find(pP => pP.id === p.id)}
                                                                campaignStations={campaignStations.filter(cS => includes(p.stationsPlan, cS.stationId))}
                                                                stations={stations}
                                                                stationType={stationType}
                                                                campaign={campaign}
                                                                campaignEvents={campaignEvents}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Accordion>
                                            ))
                                        }
                                    </Stack>
                                )}
                                {tab === CARTO && (<CartoStation campaignStations={campaignStations} stations={stations} stationType={stationType} />)}
                            </>
                        )
                    }
                </SimpleTabList>
                <ExportModal
                    isOpen={isExportOpen}
                    close={closeExport}

                    plannings={plannings}
                    planningsProgress={planningsProgress}
                    campaignStations={campaignStations}
                    stations={stations}
                    objectiveList={objectiveList}
                />
            </Grid>
        </Grid>
    )
}

CampaignTracking.propTypes = {
    stationType: PropTypes.oneOf(['piezometry', 'pluviometry', 'hydrometry', 'installation']),
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerLight)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoInstallation)),
    ]),
}

export default CampaignTracking
